import { Injectable } from '@angular/core';
import { EMPTY, iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DocumentStatusErrorCodes } from 'src/app/core/models/document-capture';
import { getErrorKey, MiddlewareError } from '../../models/error';
import { CustomNotificationId } from '../../models/notification-pg';
import { DocumentCaptureContextService } from '../context/document-capture-context.service';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { NavigationHelper } from '../core/navigation-helper.service';
import { UiHelperService } from '../core/ui-helper.service';
import { NotificationHelperService } from './notification-helper.service';
import * as moment from 'moment';
import { isInTheFuture } from '../../utils/utils';
import { AllFeatureFlags } from '@models/mmobile';

export enum DocumentState {
  VALID,
  NOT_FOUND,
  ALMOST_EXPIRED,
  EXPIRED
}

@Injectable({
  providedIn: 'root'
})
export class DocumentCaptureHelperService {
  constructor(
    private documentCaptureCtx: DocumentCaptureContextService,
    private notificationHelper: NotificationHelperService,
    private authMirror: AuthMirrorService,
    private ui: UiHelperService,
    private nav: NavigationHelper,
    private mmobileHelper: MMobileHelperService
  ) {}

  getDocumentState() {
    if (!this.isCaptureDocumentFeatureEnabled()) {
      return EMPTY;
    }

    const documentNumber = this.authMirror.authService.getDocumentNumber();
    return this.documentCaptureCtx.getExpirationDateByDNI(documentNumber).pipe(
      map(({ expirationDate }) => {
        const daysAlmostExpired =
          this.mmobileHelper.getCustomConfigData(
            'DOCUMENT_CAPTURE',
            'daysAlmostExpired'
          ) || 60;

        return this.calculateDocumentState(expirationDate, daysAlmostExpired);
      }),
      catchError(
        (error: MiddlewareError<DocumentStatusErrorCodes>) =>
          getErrorKey(error) === DocumentStatusErrorCodes.DOCUMENT_NOT_FOUND
            ? of(DocumentState.NOT_FOUND)
            : throwError(error)
      )
    );
  }

  updateDocumentCaptureNotifications() {
    if (!this.isCaptureDocumentFeatureEnabled()) {
      return EMPTY;
    }

    return this.getDocumentState().pipe(
      catchError(() => of(null)),
      tap((expirationType: DocumentState) => {
        switch (expirationType) {
          case DocumentState.NOT_FOUND:
            this.notificationHelper.updateCustomNotification({
              id: CustomNotificationId.NOTIFICATION_CAPTURE_DOCUMENT_NOT_FOUND
            });
            break;
          case DocumentState.ALMOST_EXPIRED:
            this.notificationHelper.updateCustomNotification(
              {id: CustomNotificationId.NOTIFICATION_CAPTURE_DOCUMENT_ALMOST_EXPIRED}
            );
            break;
          case DocumentState.EXPIRED:
            this.notificationHelper.updateCustomNotification(
              {id: CustomNotificationId.NOTIFICATION_CAPTURE_DOCUMENT_EXPIRED}
            );
            break;
          case DocumentState.VALID:
          default:
            break;
        }
      })
    );
  }

  showDialog() {
    this.ui.presentProfileCustomAlert(
      'Tu documento DNI/NIE todavía no está digitalizado',
      'assets/images/profile/capture-warning.png',
      'Es necesario que digitalices el documento',
      '',
      () => this.nav.toNewWithoutState(['update-doc']),
      'Digitalizar',
      null,
      'En otro momento',
      ['custom-profile-alert-message--big-image']
    );
  }

  private calculateDocumentState(
    expirationDate: string,
    daysAlmostExpired: number
  ) {
    const isExpired = !isInTheFuture(expirationDate);
    if (isExpired) return DocumentState.EXPIRED;

    const dateAlmostExpired = moment(expirationDate).subtract(
      daysAlmostExpired,
      'days'
    );
    const isAlmostExpired = !isInTheFuture(dateAlmostExpired);
    if (isAlmostExpired) return DocumentState.ALMOST_EXPIRED;

    return DocumentState.VALID;
  }

  private isCaptureDocumentFeatureEnabled() {
    return this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.DOCUMENT_CAPTURE);
  }

}
